import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

const DEFAULT_MAX_DURATION_SECONDS = 600;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default class extends Controller {
  static targets = [
    "recordButton",
    "recordButtonLabel",
    "recordStartIcon",
    "recordStopIcon",
    "recordingStatus",
    "recordingTimer",
    "error",
  ];

  static values = {
    maxDurationSeconds: { type: Number, default: DEFAULT_MAX_DURATION_SECONDS },
    directUploadUrl: String,
    micDeniedMessage: String,
    durationErrorMessage: String,
    fileTooLargeMessage: String,
  };

  connect() {
    this.chunks = [];
    this.recordedBlob = null;
    this.detectRecordingSupport();
  }

  disconnect() {
    this.hideRecordingState();
    this.stopStream();
  }

  toggleRecording(event) {
    event?.preventDefault?.();

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  async startRecording() {
    this.clearError();

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(this.stream);
      this.chunks = [];
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.chunks.push(event.data);
      };
      this.mediaRecorder.onstop = () => this.recordingStopped();
      this.mediaRecorder.start();
      this.showRecordingState();
      this.dispatch("start");
    } catch (_error) {
      this.showError(this.micDeniedMessageValue);
      this.dispatch("error", { detail: { message: this.micDeniedMessageValue } });
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
  }

  async recordingStopped() {
    this.stopStream();
    this.hideRecordingState();

    this.recordedBlob = new Blob(this.chunks, { type: this.recordingMimeType() });
    if (!(await this.validateFile(this.recordedBlob))) {
      this.dispatch("error", { detail: { message: this.errorTarget.textContent } });
      return;
    }

    this.dispatch("complete", { detail: { blob: this.recordedBlob } });
    await this.uploadBlob(this.recordedBlob, "recording.webm");
  }

  async uploadFile(file) {
    this.clearError();
    if (!(await this.validateFile(file))) {
      this.dispatch("error", { detail: { message: this.errorTarget.textContent } });
      return false;
    }

    return await this.uploadBlob(file, file.name || "upload");
  }

  async uploadBlob(blob, filename) {
    const file = new File([blob], filename, {
      type: blob.type || "audio/webm",
    });
    const upload = new DirectUpload(file, this.directUploadUrlValue);

    return await new Promise((resolve) => {
      upload.create((error, attributes) => {
        if (error) {
          this.showError(error);
          this.dispatch("error", { detail: { message: error } });
          resolve(false);
          return;
        }
        this.dispatch("uploaded", {
          detail: { signedId: attributes.signed_id, blob: blob },
        });
        resolve(true);
      });
    });
  }

  async validateFile(file) {
    if (file.size > MAX_FILE_SIZE) {
      this.showError(this.fileTooLargeMessageValue);
      return false;
    }

    const duration = await this.durationFor(file);
    if (duration > this.maxDurationSecondsValue) {
      this.showError(this.durationErrorMessageValue);
      return false;
    }

    return true;
  }

  durationFor(file) {
    return new Promise((resolve) => {
      const audio = document.createElement("audio");
      const url = URL.createObjectURL(file);
      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(audio.duration);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0);
      };
      audio.src = url;
    });
  }

  detectRecordingSupport() {
    if (window.MediaRecorder && navigator.mediaDevices?.getUserMedia) return;

    this.dispatch("unsupported");
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  stopStream() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  showRecordingState() {
    if (!this.hasRecordButtonTarget) return;

    this.recordingStartedAt = Date.now();
    if (this.hasRecordButtonLabelTarget) {
      this.recordButtonLabelTarget.textContent =
        this.recordButtonTarget.dataset.stopLabel;
    }
    this.recordButtonTarget.classList.add(
      "!bg-error",
      "!text-content-inverse",
      "hover:!bg-error-dark",
      "focus:!ring-error",
    );
    if (this.hasRecordStartIconTarget) this.recordStartIconTarget.classList.add("hidden");
    if (this.hasRecordStopIconTarget) this.recordStopIconTarget.classList.remove("hidden");
    if (this.hasRecordingStatusTarget) {
      this.recordingStatusTarget.classList.remove("hidden");
      this.recordingStatusTarget.classList.add("inline-flex");
    }
    this.updateRecordingTimer();
    this.recordingTimer = window.setInterval(
      () => this.updateRecordingTimer(),
      1000,
    );
  }

  hideRecordingState() {
    if (!this.hasRecordButtonTarget) return;

    if (this.hasRecordButtonLabelTarget) {
      this.recordButtonLabelTarget.textContent =
        this.recordButtonTarget.dataset.startLabel;
    }
    this.recordButtonTarget.classList.remove(
      "!bg-error",
      "!text-content-inverse",
      "hover:!bg-error-dark",
      "focus:!ring-error",
    );
    if (this.hasRecordStartIconTarget) this.recordStartIconTarget.classList.remove("hidden");
    if (this.hasRecordStopIconTarget) this.recordStopIconTarget.classList.add("hidden");
    if (this.hasRecordingStatusTarget) {
      this.recordingStatusTarget.classList.add("hidden");
      this.recordingStatusTarget.classList.remove("inline-flex");
    }
    if (this.hasRecordingTimerTarget) this.recordingTimerTarget.textContent = "0:00";

    if (this.recordingTimer) {
      window.clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
  }

  updateRecordingTimer() {
    const elapsedSeconds = Math.floor(
      (Date.now() - this.recordingStartedAt) / 1000,
    );
    if (this.hasRecordingTimerTarget) {
      this.recordingTimerTarget.textContent =
        this.formatRecordingDuration(elapsedSeconds);
    }

    if (elapsedSeconds >= this.maxDurationSecondsValue) {
      this.stopRecording();
    }
  }

  formatRecordingDuration(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = String(seconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  }

  recordingMimeType() {
    if (window.MediaRecorder?.isTypeSupported?.("audio/webm")) return "audio/webm";
    return "audio/mp4";
  }
}
